import React, { useMemo, useState } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Button,
  Menu,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { Search, RefreshCw, Download, ChevronDown, Info } from 'lucide-react';
import {
  ALL_DIMENSIONS,
  DIMENSION_LABELS,
  RUN_TYPE_LABELS,
  STATUS_LABELS,
  WINDOW_OPTIONS,
  ORDER_BY_OPTIONS,
} from './usageFormat';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from './UsageAnalyticsPanel.module.scss';

const MAX_GROUP_BY = 4;

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const NO_DIMENSIONS = [];

/**
 * Filters for the usage screens. What differs per surface is passed in:
 *   - hiddenDimensions: group-by keys that are constant on this surface (e.g.
 *     `company` for a company admin) — hiding them avoids chips that do nothing
 *   - showCompanyFilter + companies + companyLabelFn: the Company drill-in
 *   - partners + partnerId + onPartnerChange: the Partner filter (platform only)
 *   - dateTooltip: info next to the date range (partner attribution note)
 */
const UsageFiltersBar = ({
  hiddenDimensions = NO_DIMENSIONS,
  showCompanyFilter = false,
  companyLabelFn = null,
  partners = null,
  partnerId = '',
  onPartnerChange,
  dateTooltip = null,
  dimensions,
  windowValue,
  onWindowChange,
  dateRange,
  onDateRangeChange,
  runType,
  onRunTypeChange,
  status,
  onStatusChange,
  search,
  onSearchChange,
  groupBy,
  onGroupByChange,
  orderBy,
  onOrderByChange,
  orderDir,
  onOrderDirChange,
  splitBy,
  onSplitByChange,
  companies,
  companyId,
  onCompanyChange,
  loading,
  onRefresh,
  onExport,
}) => {
  const [exportAnchor, setExportAnchor] = useState(null);

  const availableDimensions = useMemo(() => {
    const keys = Array.isArray(dimensions?.dimensions)
      ? dimensions.dimensions.map((d) => d.key)
      : ALL_DIMENSIONS;
    // A dimension that's constant on this surface is dropped from group_by by
    // the backend, so hiding it avoids a chip that silently does nothing.
    return keys.filter((k) => !hiddenDimensions.includes(k));
  }, [dimensions, hiddenDimensions]);

  const runTypeOptions = useMemo(() => {
    const list = Array.isArray(dimensions?.run_types) ? dimensions.run_types : Object.keys(RUN_TYPE_LABELS).filter((k) => k !== 'all');
    return ['all', ...list.filter((v) => v !== 'all')];
  }, [dimensions]);

  const statusOptions = useMemo(
    () => (Array.isArray(dimensions?.statuses) ? dimensions.statuses : Object.keys(STATUS_LABELS)),
    [dimensions]
  );

  const orderByOptions = useMemo(
    () =>
      Array.isArray(dimensions?.order_by)
        ? dimensions.order_by.map((v) => ({ value: v, label: ORDER_BY_OPTIONS.find((o) => o.value === v)?.label || v }))
        : ORDER_BY_OPTIONS,
    [dimensions]
  );

  const isCustomRange = Boolean(dateRange?.start && dateRange?.end);

  const toggleDimension = (dim) => {
    const active = groupBy.includes(dim);
    if (active) {
      if (groupBy.length <= 1) return; // keep at least one grouping dimension
      onGroupByChange(groupBy.filter((d) => d !== dim));
    } else {
      if (groupBy.length >= MAX_GROUP_BY) return;
      onGroupByChange([...groupBy, dim]);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Box className={sharedStyles.toolbar}>
        <Box className={sharedStyles.filterGroup}>
          <TextField
            size="small"
            placeholder="Search agent / model…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Date range</InputLabel>
            <Select
              label="Date range"
              value={isCustomRange ? 'custom' : windowValue}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'custom') {
                  onDateRangeChange({ start: daysAgoStr(30), end: todayStr() });
                } else {
                  onWindowChange(v);
                }
              }}
            >
              {WINDOW_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
              <MenuItem value="custom">Custom range…</MenuItem>
            </Select>
          </FormControl>
          {dateTooltip && (
            <Tooltip title={dateTooltip} arrow>
              <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary' }}>
                <Info size={16} />
              </Box>
            </Tooltip>
          )}

          {isCustomRange && (
            <>
              <TextField
                size="small"
                type="date"
                label="Start"
                InputLabelProps={{ shrink: true }}
                value={dateRange.start}
                onChange={(e) => onDateRangeChange({ ...dateRange, start: e.target.value })}
              />
              <TextField
                size="small"
                type="date"
                label="End"
                InputLabelProps={{ shrink: true }}
                value={dateRange.end}
                onChange={(e) => onDateRangeChange({ ...dateRange, end: e.target.value })}
              />
            </>
          )}

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Run type</InputLabel>
            <Select label="Run type" value={runType} onChange={(e) => onRunTypeChange(e.target.value)}>
              {runTypeOptions.map((v) => (
                <MenuItem key={v} value={v}>
                  {RUN_TYPE_LABELS[v] || v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => onStatusChange(e.target.value)}>
              {statusOptions.map((v) => (
                <MenuItem key={v} value={v}>
                  {STATUS_LABELS[v] || v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {partners && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Partner</InputLabel>
              <Select label="Partner" value={partnerId} onChange={(e) => onPartnerChange(e.target.value)}>
                <MenuItem value="">All partners</MenuItem>
                {partners.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {showCompanyFilter && (
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Company</InputLabel>
              <Select label="Company" value={companyId} onChange={(e) => onCompanyChange(e.target.value)}>
                <MenuItem value="">All companies</MenuItem>
                {companies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {companyLabelFn ? companyLabelFn(c.id, c.name) : c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Tooltip title="Refresh">
            <IconButton className={sharedStyles.subtleBtn} onClick={onRefresh} disabled={loading}>
              <RefreshCw size={16} />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            className={sharedStyles.outlineBtn}
            startIcon={<Download size={16} />}
            endIcon={<ChevronDown size={14} />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
          >
            Export
          </Button>
          <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)}>
            {[
              { mode: 'breakdown', format: 'csv', label: 'Breakdown · CSV' },
              { mode: 'breakdown', format: 'json', label: 'Breakdown · JSON' },
              { mode: 'runs', format: 'csv', label: 'Runs · CSV' },
              { mode: 'runs', format: 'json', label: 'Runs · JSON' },
            ].map((item) => (
              <MenuItem
                key={item.label}
                onClick={() => {
                  setExportAnchor(null);
                  onExport(item.mode, item.format);
                }}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      <Box className={sharedStyles.toolbar}>
        <Box className={styles.chipBar}>
          <span className={styles.chipBarLabel}>Group by:</span>
          {availableDimensions.map((dim) => {
            const active = groupBy.includes(dim);
            return (
              <button
                type="button"
                key={dim}
                className={`${styles.chip} ${active ? styles.chipActive : ''}`}
                onClick={() => toggleDimension(dim)}
              >
                {DIMENSION_LABELS[dim] || dim}
              </button>
            );
          })}
        </Box>

        <Box className={sharedStyles.filterGroup}>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Sort by</InputLabel>
            <Select label="Sort by" value={orderBy} onChange={(e) => onOrderByChange(e.target.value)}>
              {orderByOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Order</InputLabel>
            <Select label="Order" value={orderDir} onChange={(e) => onOrderDirChange(e.target.value)}>
              <MenuItem value="desc">Desc</MenuItem>
              <MenuItem value="asc">Asc</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Split chart by</InputLabel>
            <Select label="Split chart by" value={splitBy} onChange={(e) => onSplitByChange(e.target.value)}>
              <MenuItem value="">None</MenuItem>
              {availableDimensions
                .filter((d) => d !== 'date')
                .map((dim) => (
                  <MenuItem key={dim} value={dim}>
                    {DIMENSION_LABELS[dim] || dim}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default UsageFiltersBar;
